import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  useAddTeamMutation,
  useUpdateTeamMutation,
} from "../api/teamApi";
import { TeamProfile } from "../types/team";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  profile?: TeamProfile | null;
}

const AddEditTeamProfile: React.FC<Props> = ({ open, setModalOpen, onSuccess, profile }) => {
  const [formData, setFormData] = useState<{
    first_name: string;
    last_name: string;
    position: string;
    bio: string;
    profile_image: File | null;
    existingProfileImage?: string;
  }>({
    first_name: "",
    last_name: "",
    position: "",
    bio: "",
    profile_image: null,
    existingProfileImage: undefined,
  });

  const [addProfile] = useAddTeamMutation();
  const [updateProfile] = useUpdateTeamMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name,
        last_name: profile.last_name,
        position: profile.position,
        bio: profile.bio,
        profile_image: null,
        existingProfileImage: profile.profile_image,
      });
    } else {
      setFormData({
        first_name: "",
        last_name: "",
        position: "",
        bio: "",
        profile_image: null,
        existingProfileImage: undefined,
      });
    }
  }, [profile, open]);

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, profile_image: file }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const fd = new FormData();
    fd.append("first_name", formData.first_name);
    fd.append("last_name", formData.last_name);
    fd.append("position", formData.position);
    fd.append("bio", formData.bio);
    if (formData.profile_image) {
      fd.append("profile_image", formData.profile_image);
    }

    try {
      if (profile?.id) {
        await updateProfile({ id: profile.id, body: fd }).unwrap();
      } else {
        await addProfile({ body: fd }).unwrap();
      }
      setModalOpen(false);
      onSuccess?.();
      setSubmissionError(null);
    } catch (err) {
      console.error("Error saving profile:", err);
      setSubmissionError("Failed to save profile. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{profile ? "Edit Profile" : "Add Profile"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          label="First Name"
          value={formData.first_name}
          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Last Name"
          value={formData.last_name}
          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Position"
          value={formData.position}
          onChange={(e) => setFormData({ ...formData, position: e.target.value })}
        />
        <TextField
          fullWidth
          margin="dense"
          label="Bio"
          multiline
          rows={3}
          value={formData.bio}
          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
        />

        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" component="label">
            Upload Profile Image
            <input hidden accept="image/*" type="file" onChange={handleProfileImageChange} />
          </Button>
          {formData.existingProfileImage && !formData.profile_image && (
            <Box mt={1}>
              <img src={formData.existingProfileImage} alt="profile" width={100} />
            </Box>
          )}
          {formData.profile_image && (
            <Box mt={1}>
              <img src={URL.createObjectURL(formData.profile_image)} alt="preview" width={100} />
            </Box>
          )}
        </Box>

        {submissionError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submissionError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setModalOpen(false)} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditTeamProfile;
